import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormRow } from '@gravity-ui/components';
import { DatePicker } from '@gravity-ui/date-components';
import { dateTimeParse, settings } from '@gravity-ui/date-utils';
import { ChevronDown, ChevronUp, CircleInfoFill } from '@gravity-ui/icons';
import { Button, Label, Modal, TextArea } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';
import { format } from 'date-fns';

import { useSafeParams } from 'hooks';
import { useGetBlankQuery, useUpsertPatientEstimateMutation } from 'store/api';
import type { Problem } from 'store/types/common';

import { ProblemAssessmentForm } from './utils/problem-assessment-form';
import type { StepThreeInfo } from './utils/use-validation';
import { useStepThreeValidation } from './utils/use-validation';
import {
	ACHIEVEMENTS,
	ASSESSMENT,
	CANCEL,
	DATA_INTERVIEW,
	DEADLINES,
	EVALUATE_ACTIVITY,
	EXAMPLE_GOAL,
	FINAL_SAVE,
	GOAL,
	GOAL_PLAN,
	GOAL_TITLE,
	HINTS,
	HINTS_QUESTIONS,
	PLACEHOLDER_GOAL,
	RESULT,
	SCALE,
	STEPS_PLAN,
	TITLE_THREE,
} from './constants';

import styles from './styles.module.scss';
import { useMediaQuery } from '@mui/material';

const defaultProblems = [
	{
		execution: '',
		satisfaction: '',
		// prefix: [''],
		// domainCode: [''],
		dateNextInterview: '',
	},
	{
		execution: '',
		satisfaction: '',
		// prefix: [''],
		// domainCode: [''],
		dateNextInterview: '',
	},
	{
		execution: '',
		satisfaction: '',
		// prefix: [''],
		// domainCode: [''],
		dateNextInterview: '',
	},
];

export const ClientBlankEstimateAdd = ({ open, setOpen, onAdd }: any) => {
	const [isHelpUpperVisible, setHelpUpperVisible] = useState(true);
	const [isHelpLowerVisible, setHelpLowerVisible] = useState(true);
	const [loading, setLoading] = useState(true);

	const isAdaptive = useMediaQuery('(max-width: 953px)');

	const [lang, setLang] = useState<boolean>(false);

	const { clientId } = useSafeParams();

	const { data } = useGetBlankQuery(clientId ?? skipToken, {
		refetchOnMountOrArgChange: true,
	});

	const [upsertEstimate] = useUpsertPatientEstimateMutation();

	const estimateHistories = data?.problems || [];

	const sortedProblems = useMemo(() => {
		return (
			estimateHistories
				?.slice()
				.sort((a, b) => b.importance - a.importance)
				.slice(0, 5) || []
		);
	}, []);

	useEffect(() => {
		if (data) {
			console.log('новая оценка', estimateHistories);
			console.log('id', data.id);
		}
	}, [data]);

	const { stepThreeSchema } = useStepThreeValidation();

	const methods = useForm<StepThreeInfo>({
		resolver: zodResolver(stepThreeSchema),
		defaultValues: {
			patientCardId: data?.id,
			goal: '',
			estimateHistories:
				sortedProblems.length > 0
					? sortedProblems.map((problem: Problem) => ({
							problemId: problem.id,
							execution: '',
							satisfaction: '',
							// prefix: [],
							// domainCode: [],
							dateNextInterview: '',
						}))
					: defaultProblems,
		},
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isSubmitted, errors, isValid },
	} = methods;

	const formValues = watch();

	const toggleHelpUpperVisibility = () => {
		setHelpUpperVisible(!isHelpUpperVisible);
	};

	const toggleHelpLowerVisibility = () => {
		setHelpLowerVisible(!isHelpLowerVisible);
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			setLoading(false);
		}, 2000);

		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		settings.loadLocale('ru').then(() => {
			settings.setLocale('ru');
			setLang(true);
		});
	}, []);

	const onSubmit = async (data: StepThreeInfo) => {
		try {
			const transformedData = {
				...data,
				dateNextInterview: data.dateNextInterview ?? null,
				estimateHistories: data.estimateHistories.map((history) => ({
					...history,
				})),
			};
			console.log(transformedData.estimateHistories);
			await upsertEstimate(transformedData).unwrap();
			await onAdd();
			toast('Новые оценки успешно добавлены');
			window.location.reload();
			setOpen(false);
		} catch {
			console.log(errors);
			toast('Не удалось добавить оценку');
		}
	};

	useEffect(() => {
		if (data) {
			const patientEstimate = data.patientEstimates?.[0];
			setValue('goal', patientEstimate?.goal || '');
			// setValue('goalPlan', patientEstimate?.goal || '');
		}
	}, [data, setValue]);

	return (
		<>
			{!loading && (
				<Modal open={open} className={styles.container}>
					{isAdaptive ? (
						<div className={styles.container}>
							<FormProvider {...methods}>
								<form onSubmit={handleSubmit(onSubmit)}>
									<div className={styles.info}>
										<div className={styles.info__title}>
											<h3>{TITLE_THREE}</h3>
											{/* <Label theme="info" size="m">
												{ASSESSMENT}
											</Label> */}
										</div>

										<div className={`${styles.help} ${isHelpUpperVisible ? '' : styles.hidden}`}>
											<div className={styles.help__title}>
												<CircleInfoFill className={styles.help__title__svg} />
												<h4>{HINTS}</h4>
												<Button onClick={toggleHelpUpperVisibility} view="flat">
													{' '}
													{isHelpUpperVisible ? <ChevronUp /> : <ChevronDown />}
												</Button>
											</div>
											<div
												className={`${styles.help__list} ${isHelpUpperVisible ? '' : styles.hidden}`}
											>
												<div>
													<h4>{EVALUATE_ACTIVITY}</h4>
													<span>{SCALE}</span>
												</div>
											</div>
										</div>

										{sortedProblems.map((problem: Problem, index: number) => (
											<ProblemAssessmentForm
												key={problem.id || index}
												index={index}
												problemText={problem.text}
												problemImportance={problem.importance}
											/>
										))}

										<h4>{GOAL_TITLE}</h4>
										<div className={`${styles.help} ${isHelpLowerVisible ? '' : styles.hidden}`}>
											<div className={styles.help__title}>
												<CircleInfoFill className={styles.help__title__svg} />
												<h4>{HINTS}</h4>
												<Button onClick={toggleHelpLowerVisibility} view="flat">
													{' '}
													{isHelpLowerVisible ? <ChevronUp /> : <ChevronDown />}
												</Button>
											</div>
											<div
												className={`${styles.help__list} ${isHelpLowerVisible ? '' : styles.hidden}`}
											>
												<ol>
													{HINTS_QUESTIONS}
													<ul>
														<li>{ACHIEVEMENTS}</li>
														<li>{RESULT}</li>
														<li>{DEADLINES}</li>
													</ul>
												</ol>
											</div>
										</div>
										<FormRow label={GOAL} className={`${styles.info__row} ${styles.top}`}>
											<TextArea
												size="xl"
												hasClear
												minRows={4}
												placeholder={PLACEHOLDER_GOAL}
												note={<span>{EXAMPLE_GOAL}</span>}
												{...methods.register('goal')}
												validationState={isSubmitted && errors.goal ? 'invalid' : undefined}
												errorPlacement="inside"
												errorMessage={
													isSubmitted && errors.goal
														? 'Обязательно для заполнения'
														: undefined
												}
											/>
										</FormRow>

										<FormRow className={styles.info__row} label={DATA_INTERVIEW}>
											{lang && (
												<DatePicker
													size="xl"
													hasClear
													timeZone="Europe/Moscow"
													{...methods.register('dateNextInterview')}
													defaultValue={
														formValues.dateNextInterview
															? dateTimeParse(formValues.dateNextInterview)
															: undefined
													}
													onUpdate={(value) => {
														if (!value) {
															setValue('dateNextInterview', '');
															return;
														}
														const jsDate = value.toDate();
														const formatted = format(jsDate, 'yyyy-MM-dd');
														setValue('dateNextInterview', formatted);
													}}
													validationState={
														isSubmitted && errors.dateNextInterview ? 'invalid' : undefined
													}
													errorPlacement="inside"
													errorMessage={
														isSubmitted && errors.dateNextInterview
															? errors.dateNextInterview.message || 'Некорректная дата'
															: undefined
													}
												/>
											)}
										</FormRow>

										<div className={styles.info__btn_group}>
											<Button size="xl" onClick={() => setOpen(false)}>
												{CANCEL}
											</Button>

											<Button
												size="xl"
												type="submit"
												// disabled={!isValid}
												// view={isValid ? 'action' : 'raised'}
												className={styles.info__btn_group__save}
											>
												{FINAL_SAVE}
											</Button>
										</div>
									</div>
								</form>
							</FormProvider>
						</div>
					) : (
						<div className={styles.container}>
							<FormProvider {...methods}>
								<form onSubmit={handleSubmit(onSubmit)}>
									<div className={styles.info}>
										<div className={styles.info__title}>
											<h3>{TITLE_THREE}</h3>
											{/* <Label theme="info" size="m">
												{ASSESSMENT}
											</Label> */}
										</div>

										{sortedProblems.map((problem: Problem, index: number) => (
											<ProblemAssessmentForm
												key={problem.id || index}
												index={index}
												problemText={problem.text}
												problemImportance={problem.importance}
											/>
										))}

										<h4>{GOAL_TITLE}</h4>
										<FormRow label={GOAL} className={`${styles.info__row} ${styles.top}`}>
											<TextArea
												size="xl"
												hasClear
												minRows={4}
												placeholder={PLACEHOLDER_GOAL}
												note={<span>{EXAMPLE_GOAL}</span>}
												{...methods.register('goal')}
												validationState={isSubmitted && errors.goal ? 'invalid' : undefined}
												errorPlacement="inside"
												errorMessage={
													isSubmitted && errors.goal
														? 'Обязательно для заполнения'
														: undefined
												}
											/>
										</FormRow>

										<FormRow className={styles.info__row} label={DATA_INTERVIEW}>
											{lang && (
												<DatePicker
													size="xl"
													hasClear
													timeZone="Europe/Moscow"
													{...methods.register('dateNextInterview')}
													defaultValue={
														formValues.dateNextInterview
															? dateTimeParse(formValues.dateNextInterview)
															: undefined
													}
													onUpdate={(value) => {
														if (!value) {
															setValue('dateNextInterview', '');
															return;
														}
														const jsDate = value.toDate();
														const formatted = format(jsDate, 'yyyy-MM-dd');
														setValue('dateNextInterview', formatted);
													}}
													validationState={
														isSubmitted && errors.dateNextInterview ? 'invalid' : undefined
													}
													errorPlacement="inside"
													errorMessage={
														isSubmitted && errors.dateNextInterview
															? errors.dateNextInterview.message || 'Некорректная дата'
															: undefined
													}
												/>
											)}
										</FormRow>

										<div className={styles.info__btn_group}>
											<Button size="xl" onClick={() => setOpen(false)}>
												{CANCEL}
											</Button>

											<Button
												size="xl"
												type="submit"
												// disabled={!isValid}
												// view={isValid ? 'action' : 'raised'}
												className={styles.info__btn_group__save}
											>
												{FINAL_SAVE}
											</Button>
										</div>
									</div>
								</form>
							</FormProvider>

							<div className={styles.helpers}>
								<div className={`${styles.help} ${isHelpUpperVisible ? '' : styles.hidden}`}>
									<div className={styles.help__title}>
										<CircleInfoFill className={styles.help__title__svg} />
										<h4>{HINTS}</h4>
										<Button onClick={toggleHelpUpperVisibility} view="flat">
											{' '}
											{isHelpUpperVisible ? <ChevronUp /> : <ChevronDown />}
										</Button>
									</div>
									<div className={`${styles.help__list} ${isHelpUpperVisible ? '' : styles.hidden}`}>
										<div>
											<h4>{EVALUATE_ACTIVITY}</h4>
											<span>{SCALE}</span>
										</div>
									</div>
								</div>
								<div className={`${styles.help} ${isHelpLowerVisible ? '' : styles.hidden}`}>
									<div className={styles.help__title}>
										<CircleInfoFill className={styles.help__title__svg} />
										<h4>{HINTS}</h4>
										<Button onClick={toggleHelpLowerVisibility} view="flat">
											{' '}
											{isHelpLowerVisible ? <ChevronUp /> : <ChevronDown />}
										</Button>
									</div>
									<div className={`${styles.help__list} ${isHelpLowerVisible ? '' : styles.hidden}`}>
										<ol>
											{HINTS_QUESTIONS}
											<ul>
												<li>{ACHIEVEMENTS}</li>
												<li>{RESULT}</li>
												<li>{DEADLINES}</li>
											</ul>
										</ol>
									</div>
								</div>
							</div>
						</div>
					)}
				</Modal>
			)}
		</>
	);
};
